'use client';

import { Fragment, useMemo } from 'react';
import moment from 'moment';
import { get as _get, find as _find, uniq as _uniq } from 'lodash-es';
import { useFormikContext } from 'formik';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useAppContext } from '../../contexts/app';
import { DISCORD_ENABLED } from '../../constants/DISCORD_ENABLED';
import { myGroupsQuery } from '../../apollo/groups';
import { cityHasDiscordConnectionsQuery } from '../../apollo/discord';
import { eventTypes } from '../../utils/events';
import HostUser from '../host-user';
import HostGroup from '../host-group';
import type { EventFormValues } from './form';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (status: string) => void;
  isEventSeries?: boolean;
};

const PublishDialog = ({ open, onClose, onConfirm, isEventSeries }: Props) => {
  const {
    values: {
      name,
      visibility,
      registrationType,
      groupId,
      hostType,
      startDate,
      startTime,
      startLocation,
      eventType,
    },
  } = useFormikContext<EventFormValues>();

  const { isLoggedIn, loggedInUser, city } = useAppContext();

  const { data: groupsData, error: groupsError } = useQuery(myGroupsQuery, {
    skip: !isLoggedIn,
  });

  const { data: cityDiscordData } = useQuery(cityHasDiscordConnectionsQuery, {
    variables: { slug: city?.slug },
    skip: !DISCORD_ENABLED || !city?.slug || visibility !== 'PUBLIC',
  });

  const group = useMemo(() => {
    if (!groupsData || !groupId) {
      return null;
    }
    const groups = _get(groupsData, 'me.groups', []);
    return _find(groups, ({ id }: { id: string }) => id === groupId);
  }, [groupsData, groupId]);

  const groupApprovalRequired = useMemo(() => (
    group && !group.permissions.createEventWithoutApproval
  ), [group]);

  const sendToUserFollowers = useMemo(() => {
    if (isEventSeries && hostType === 'Group') {
      return false;
    }
    return visibility === 'PUBLIC';
  }, [visibility, isEventSeries, hostType]);

  const sendToGroupMembers = useMemo(() => {
    return group && (visibility === 'PUBLIC' || visibility === 'GROUP');
  }, [visibility, group]);

  const formattedDateTime = useMemo(() => {
    if (!startDate) return null;
    const dateStr = moment(startDate).format('dddd, MMMM D, YYYY');
    if (!startTime) return dateStr;
    const timeStr = moment(startTime).format('h:mm A');
    return `${dateStr} at ${timeStr}`;
  }, [startDate, startTime]);

  const showGroupDiscord = DISCORD_ENABLED && group?.hasDiscordConnections;
  const showCityDiscord = DISCORD_ENABLED
    && visibility === 'PUBLIC'
    && cityDiscordData?.city?.hasDiscordConnections;

  const discordServerNames = useMemo(() => {
    const connections = [
      ...(showGroupDiscord ? group?.discordConnections ?? [] : []),
      ...(showCityDiscord ? cityDiscordData?.city?.discordConnections ?? [] : []),
    ];
    return _uniq(connections.map((connection) => connection?.guildName).filter(Boolean));
  }, [showGroupDiscord, showCityDiscord, group, cityDiscordData]);

  const visibilityLabel = useMemo(() => {
    switch (visibility) {
      case 'PUBLIC': return 'Public';
      case 'GROUP': return group ? `Members of ${group.name}` : 'Group members only';
      case 'UNLISTED': return 'Unlisted';
      case 'PRIVATE': return 'Private';
      default: return visibility;
    }
  }, [visibility, group]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Publish {eventTypes[eventType].name}</DialogTitle>
      <DialogContent>
        <Typography variant="h6" gutterBottom>{name || 'Untitled'}</Typography>
        {formattedDateTime && (
          <Typography variant="body2" color="text.secondary">
            {formattedDateTime}
          </Typography>
        )}
        {eventType !== 'ONLINE_EVENT' && startLocation && (
          <Typography variant="body2" color="text.secondary">
            {startLocation}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {visibilityLabel}
        </Typography>

        {registrationType === 'APPROVAL_REQUIRED' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Attendees will need to request approval — location and ride details are hidden until their request is approved.
          </Alert>
        )}
        {registrationType === 'EXTERNAL_REGISTRATION' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Attendees can RSVP here to track their interest, but will be directed to also register at the external URL.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Email notifications will be sent to:
        </Typography>
        {groupsError && groupId && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Could not load group information. Notification details may be incomplete.
          </Alert>
        )}
        {(sendToUserFollowers || sendToGroupMembers) ? (
          <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {sendToUserFollowers && (
              <Fragment>
                Followers of&nbsp;<HostUser user={loggedInUser} simple />
              </Fragment>
            )}
            {sendToUserFollowers && sendToGroupMembers && (
              <span>&nbsp;and&nbsp;</span>
            )}
            {sendToGroupMembers && (
              <Fragment>
                Members of&nbsp;<HostGroup group={group} simple />
              </Fragment>
            )}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No notifications will be sent.
          </Typography>
        )}

        {(showGroupDiscord || showCityDiscord) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {discordServerNames.length
              ? `This event will also be posted to Discord in ${new Intl.ListFormat('en').format(discordServerNames)}.`
              : 'This event will also be posted to connected Discord channels.'}
          </Typography>
        )}

        {groupApprovalRequired && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {group.name} requires events to be approved before publishing.
            This event will be submitted for group admin approval.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onConfirm(groupApprovalRequired ? 'GROUP_PENDING' : 'PUBLISHED')}
          variant="contained"
          color="primary"
        >
          {groupApprovalRequired ? 'Submit for Approval' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishDialog;
